import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { I18nextProvider } from "react-i18next";
import i18n from "./lib/i18n";
import App from "./App.jsx";
import { GlobalErrorFallback } from "./components/GlobalErrorFallback.jsx";
import { initAnalytics, captureUTMParams } from "./lib/analytics";
import {
  clearStaleChunkReloadGuard,
  isChunkLoadError,
  tryReloadForStaleChunk,
} from "./lib/chunkLoadError";
import "./index.css";

clearStaleChunkReloadGuard();

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason) && tryReloadForStaleChunk()) {
      event.preventDefault();
    }
  });
}

// Initialize Sentry for client-side error tracking
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Capture UTM params on load (for conversion funnel) — runs regardless of consent
captureUTMParams();

// Initialize PostHog product analytics ONLY if user has consented (GDPR)
if (localStorage.getItem("fcp_analytics_consent") === "true") {
  initAnalytics();
}

// Remove prerender-hide style so the shell is invisible until React takes over
document.getElementById('prerender-hide')?.remove();

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Sentry.ErrorBoundary fallback={({ resetError }) => <GlobalErrorFallback resetError={resetError} />}>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Sentry.ErrorBoundary>
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  </React.StrictMode>
);
